import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, RefreshCw, Network, Link2, Trash2, Play, ChevronRight, CheckCircle, AlertCircle, Clock, XCircle, Shield, Pencil, Info, Activity } from 'lucide-react';
import StatusBadge from '../components/common/StatusBadge';
import ConfirmDialog from '../components/common/ConfirmDialog';
import StatCard from '../components/common/StatCard';
import { useToast } from '../components/common/ToastContainer';
import { edgePeeringService, edgeService } from '../services';
import type { EdgePeering, Edge } from '../services';
import type { WanInterface, SyncResult } from '../services/edgePeering.service';
import mtgeService from '../services/mtge.service';
import connectorService from '../services/connector.service';
import type { Connector } from '../services/connector.service';

import { formatDate } from '../utils/formatDate';
import { usePermissions } from '../hooks/usePermissions';
import { useTenantStore } from '../stores/tenantStore';

// Modal for creating/editing peering
interface PeeringModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  edges: Edge[];
  initialData?: EdgePeering;
}

const PeeringModal = ({ isOpen, onClose, onSubmit, edges, initialData }: PeeringModalProps) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    topology_type: initialData?.topology_type || 'mesh',
    hub_edge_id: initialData?.hub_edge_id || '',
    // VSNP-1019: no hardcoded default. This field is display-only and the
    // subnet is allocated by the API from the tenant's address space. The old
    // fallback shipped 100.99.0.0/24 to every tenant regardless of its own
    // allocation, which is how this tenant ended up peering outside its space
    // while the field above it claimed the value was "allocated".
    tunnel_subnet: initialData?.tunnel_subnet || '',
    listen_port: initialData?.listen_port || 51822,
    bgp_enabled: !!(initialData?.bgp_enabled ?? true),
    bgp_mode: initialData?.bgp_mode || 'ibgp',
    base_asn: initialData?.base_asn || 65000,
    bfd_enabled: !!(initialData?.bfd_enabled ?? true),
    redundancy_mode: initialData?.redundancy_mode || 'single',
    // VSNP-1019: same — allocated from the tenant's secondary peering subnet.
    secondary_tunnel_subnet: initialData?.secondary_tunnel_subnet || '',
    secondary_listen_port: initialData?.secondary_listen_port || 51823,
    tunnel_protocol: initialData?.tunnel_protocol || 'wireguard',
    ipsec_rekey_interval_hours: initialData?.ipsec_rekey_interval_hours || 24,
    dps_enabled: !!(initialData?.dps_enabled ?? false),
    dps_probe_interval_ms: initialData?.dps_probe_interval_ms || 1000,
    dps_max_latency_ms: initialData?.dps_max_latency_ms || 150,
    dps_max_jitter_ms: initialData?.dps_max_jitter_ms || 30,
    dps_max_loss_pct: initialData?.dps_max_loss_pct || 1.0,
    dps_switch_threshold: initialData?.dps_switch_threshold || 3,
    dps_recovery_threshold: initialData?.dps_recovery_threshold || 5,
  });

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {initialData ? 'Edit Peering Topology' : 'Create Peering Topology'}
          </h2>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="flex flex-col min-h-0 flex-1">
          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Enter peering name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Topology Type
              </label>
              <select
                value={formData.topology_type}
                onChange={(e) => setFormData({ ...formData, topology_type: e.target.value as 'hub-spoke' | 'mesh' | 'partial-mesh' })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="mesh">Full Mesh</option>
                <option value="partial-mesh">Partial Mesh</option>
                <option value="hub-spoke">Hub and Spoke</option>
              </select>
            </div>

            {formData.topology_type === 'hub-spoke' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Hub Edge
                </label>
                <select
                  value={formData.hub_edge_id}
                  onChange={(e) => setFormData({ ...formData, hub_edge_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select hub edge...</option>
                  {edges.map((edge) => (
                    <option key={edge.id} value={edge.id}>{edge.devName}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Dedicated edges only. MTGE devices serve VPN clients through the IoT Gateway (wg0) backbone and pair with Connectors directly — they don't participate in Edge Peering.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tunnel Subnet
              </label>
              <input
                type="text"
                value={formData.tunnel_subnet}
                placeholder="Allocated on save"
                readOnly
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {formData.tunnel_subnet
                  ? 'Allocated from this tenant’s address space.'
                  : 'Allocated from this tenant’s address space when the peering is created.'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tunnel Protocol
              </label>
              <select
                value={formData.tunnel_protocol}
                onChange={(e) => setFormData({ ...formData, tunnel_protocol: e.target.value as 'wireguard' | 'ipsec' })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="wireguard">WireGuard</option>
                <option value="ipsec">IPSec (AES-256-GCM)</option>
              </select>
            </div>

            {formData.tunnel_protocol === 'ipsec' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rekey Interval (hours)
                </label>
                <input
                  type="number"
                  min={1}
                  value={formData.ipsec_rekey_interval_hours}
                  onChange={(e) => setFormData({ ...formData, ipsec_rekey_interval_hours: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Listen Port
                </label>
                <input
                  type="number"
                  value={formData.listen_port}
                  onChange={(e) => setFormData({ ...formData, listen_port: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Base ASN
                </label>
                <input
                  type="number"
                  value={formData.base_asn}
                  onChange={(e) => setFormData({ ...formData, base_asn: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.bgp_enabled}
                  onChange={(e) => setFormData({ ...formData, bgp_enabled: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Enable BGP</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.bfd_enabled}
                  onChange={(e) => setFormData({ ...formData, bfd_enabled: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Enable BFD</span>
              </label>
            </div>

            {formData.bgp_enabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  BGP Mode
                </label>
                <select
                  value={formData.bgp_mode}
                  onChange={(e) => setFormData({ ...formData, bgp_mode: e.target.value as 'ibgp' | 'ebgp' })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ibgp">iBGP (Internal BGP)</option>
                  <option value="ebgp">eBGP (External BGP)</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Redundancy Mode
              </label>
              <select
                value={formData.redundancy_mode}
                onChange={(e) => setFormData({ ...formData, redundancy_mode: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="single">Single Tunnel</option>
                <option value="active-standby">Active / Standby</option>
                <option value="active-active">Active / Active (ECMP)</option>
              </select>
            </div>

            {formData.redundancy_mode !== 'single' && (
              <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
                  <Shield className="w-4 h-4" />
                  Dual Tunnel Configuration
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Secondary Tunnel Subnet
                  </label>
                  <input
                    type="text"
                    value={formData.secondary_tunnel_subnet}
                    placeholder="Allocated on save"
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Allocated from this tenant’s secondary peering subnet.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Secondary Listen Port
                  </label>
                  <input
                    type="number"
                    value={formData.secondary_listen_port}
                    onChange={(e) => setFormData({ ...formData, secondary_listen_port: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Dynamic Path Selection */}
                <div className="pt-3 border-t border-blue-200 dark:border-blue-800">
                  <label className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      checked={formData.dps_enabled}
                      onChange={(e) => setFormData({ ...formData, dps_enabled: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Enable Dynamic Path Selection</span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Proactively steer traffic away from degraded paths based on latency, jitter, and packet loss SLA thresholds.
                  </p>

                  {formData.dps_enabled && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Max Latency (ms)</label>
                          <input
                            type="number"
                            value={formData.dps_max_latency_ms}
                            onChange={(e) => setFormData({ ...formData, dps_max_latency_ms: parseInt(e.target.value) || 150 })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Max Jitter (ms)</label>
                          <input
                            type="number"
                            value={formData.dps_max_jitter_ms}
                            onChange={(e) => setFormData({ ...formData, dps_max_jitter_ms: parseInt(e.target.value) || 30 })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Max Loss (%)</label>
                          <input
                            type="number"
                            step="0.1"
                            value={formData.dps_max_loss_pct}
                            onChange={(e) => setFormData({ ...formData, dps_max_loss_pct: parseFloat(e.target.value) || 1.0 })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Probe Interval (ms)</label>
                          <input
                            type="number"
                            min="100"
                            value={formData.dps_probe_interval_ms}
                            onChange={(e) => setFormData({ ...formData, dps_probe_interval_ms: parseInt(e.target.value) || 1000 })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Switch After</label>
                          <input
                            type="number"
                            min="1"
                            value={formData.dps_switch_threshold}
                            onChange={(e) => setFormData({ ...formData, dps_switch_threshold: parseInt(e.target.value) || 3 })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Recover After</label>
                          <input
                            type="number"
                            min="1"
                            value={formData.dps_recovery_threshold}
                            onChange={(e) => setFormData({ ...formData, dps_recovery_threshold: parseInt(e.target.value) || 5 })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {initialData ? 'Save Changes' : 'Create Peering'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

// Add Member Modal
interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (deviceId: string, role: string, deviceType: 'edge' | 'mtge' | 'connector', primaryWanPortId?: string, secondaryWanPortId?: string, peerWith?: string[]) => void;
  edges: Edge[];
  existingMemberIds: string[];
  existingMembers: Array<{ id: string; edge_id: string; edgeName?: string; tunnel_ip: string }>;
  topologyType: 'hub-spoke' | 'mesh' | 'partial-mesh';
  redundancyMode: 'single' | 'active-standby' | 'active-active';
}

interface MtgeOption {
  id: string;
  device_name: string;
  serial_number: string;
  wan_ip?: string;
  status: string;
}

const AddMemberModal = ({ isOpen, onClose, onSubmit, edges, existingMemberIds, existingMembers, topologyType, redundancyMode }: AddMemberModalProps) => {
  const [deviceType, setDeviceType] = useState<'edge' | 'mtge' | 'connector'>('edge');
  const [selectedEdge, setSelectedEdge] = useState('');
  const [selectedMtge, setSelectedMtge] = useState('');
  const [selectedConnector, setSelectedConnector] = useState('');
  const [mtgeOptions, setMtgeOptions] = useState<MtgeOption[]>([]);
  const [connectorOptions, setConnectorOptions] = useState<Connector[]>([]);
  const [loadingMtge, setLoadingMtge] = useState(false);
  const [loadingConnectors, setLoadingConnectors] = useState(false);
  const [role, setRole] = useState<'hub' | 'spoke' | 'mesh-peer'>(topologyType === 'hub-spoke' ? 'spoke' : 'mesh-peer');
  const [wanInterfaces, setWanInterfaces] = useState<WanInterface[]>([]);
  const [primaryWanPortId, setPrimaryWanPortId] = useState('');
  const [secondaryWanPortId, setSecondaryWanPortId] = useState('');
  const [loadingWan, setLoadingWan] = useState(false);
  const [selectedPeers, setSelectedPeers] = useState<string[]>([]);

  const availableEdges = edges.filter(e => !existingMemberIds.includes(e.id));

  // Fetch MTGEs with active tenant assignments when switching to MTGE mode
  useEffect(() => {
    if (deviceType === 'mtge' && isOpen) {
      setLoadingMtge(true);
      mtgeService.list({ status: 'active', pageSize: 100 })
        .then((response) => {
          const available = response.items.filter(m => !existingMemberIds.includes(m.id));
          setMtgeOptions(available);
        })
        .catch(() => setMtgeOptions([]))
        .finally(() => setLoadingMtge(false));
    }
  }, [deviceType, isOpen, existingMemberIds]);

  // Fetch connectors when switching to connector mode
  useEffect(() => {
    if (deviceType === 'connector' && isOpen) {
      setLoadingConnectors(true);
      connectorService.list({ status: 'active', limit: 100 })
        .then((response) => {
          const available = response.items.filter((c: Connector) => !existingMemberIds.includes(c.serial_number));
          setConnectorOptions(available);
        })
        .catch(() => setConnectorOptions([]))
        .finally(() => setLoadingConnectors(false));
    }
  }, [deviceType, isOpen, existingMemberIds]);

  // Fetch WAN interfaces for dual-tunnel edge selection
  useEffect(() => {
    if (deviceType === 'edge' && selectedEdge && redundancyMode !== 'single') {
      setLoadingWan(true);
      edgePeeringService.getWanInterfaces(selectedEdge)
        .then((interfaces) => {
          setWanInterfaces(interfaces);
          if (interfaces.length > 0) setPrimaryWanPortId(interfaces[0].id);
          if (interfaces.length > 1) setSecondaryWanPortId(interfaces[1].id);
        })
        .catch(() => setWanInterfaces([]))
        .finally(() => setLoadingWan(false));
    } else {
      setWanInterfaces([]);
      setPrimaryWanPortId('');
      setSecondaryWanPortId('');
    }
  }, [selectedEdge, redundancyMode, deviceType]);

  // Reset selection when switching device type
  useEffect(() => {
    setSelectedEdge('');
    setSelectedMtge('');
    setSelectedConnector('');
    setWanInterfaces([]);
    setPrimaryWanPortId('');
    setSecondaryWanPortId('');
    // Connectors can't be hub
    if (deviceType === 'connector' && role === 'hub') {
      setRole(topologyType === 'hub-spoke' ? 'spoke' : 'mesh-peer');
    }
  }, [deviceType]);

  if (!isOpen) return null;

  const selectedDeviceId = deviceType === 'connector' ? selectedConnector : deviceType === 'mtge' ? selectedMtge : selectedEdge;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Add Member to Peering
          </h2>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(selectedDeviceId, role, deviceType, redundancyMode !== 'single' && deviceType === 'edge' ? primaryWanPortId : undefined, redundancyMode !== 'single' && deviceType === 'edge' ? secondaryWanPortId : undefined, topologyType === 'partial-mesh' ? selectedPeers : undefined); }} className="flex flex-col min-h-0 flex-1">
          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {/* Policy banner: MTGEs not allowed in Edge Peering */}
            <div className="flex gap-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-3 text-sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
                className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400 mt-0.5"
              >
                <path
                  fillRule="evenodd"
                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="text-amber-800 dark:text-amber-100">
                <p className="font-semibold">Shared Gateways (MTGEs) cannot join Edge Peering</p>
                <p className="mt-1 text-amber-700 dark:text-amber-200">
                  MTGEs serve VPN clients over the IoT Gateway (wg0) backbone and pair with Connectors directly. For hub-spoke, mesh, or E2E IPSec peering, deploy a dedicated edge.
                </p>
              </div>
            </div>

            {/* Device Type Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Device Type
              </label>
              <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDeviceType('edge')}
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${deviceType === 'edge'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                >
                  Dedicated Edge
                </button>
                <button
                  type="button"
                  onClick={() => setDeviceType('connector')}
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${deviceType === 'connector'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                >
                  Connector
                </button>
              </div>
            </div>

            {/* Device Selection */}
            {deviceType === 'edge' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Select Edge
                </label>
                <select
                  required
                  value={selectedEdge}
                  onChange={(e) => setSelectedEdge(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select an edge...</option>
                  {availableEdges.map((edge) => (
                    <option key={edge.id} value={edge.id}>{edge.devName} ({edge.devSerNum})</option>
                  ))}
                </select>
              </div>
            ) : deviceType === 'mtge' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Select Shared Gateway
                </label>
                {loadingMtge ? (
                  <div className="text-sm text-gray-500 py-2">Loading shared gateways...</div>
                ) : mtgeOptions.length === 0 ? (
                  <div className="text-sm text-yellow-600 py-2">No active shared gateways available with tenant assignments</div>
                ) : (
                  <select
                    required
                    value={selectedMtge}
                    onChange={(e) => setSelectedMtge(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a shared gateway...</option>
                    {mtgeOptions.map((mtge) => (
                      <option key={mtge.id} value={mtge.id}>
                        {mtge.device_name} ({mtge.wan_ip || 'No WAN IP'})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Select Connector
                </label>
                {loadingConnectors ? (
                  <div className="text-sm text-gray-500 py-2">Loading connectors...</div>
                ) : connectorOptions.length === 0 ? (
                  <div className="text-sm text-yellow-600 py-2">No active connectors available</div>
                ) : (
                  <select
                    required
                    value={selectedConnector}
                    onChange={(e) => setSelectedConnector(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select a connector...</option>
                    {connectorOptions.map((conn) => (
                      <option key={conn.id} value={conn.id}>
                        {conn.connector_name} ({conn.wan_ip || 'No WAN IP'})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {topologyType === 'hub-spoke' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'hub' | 'spoke')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  disabled={deviceType === 'connector'}
                >
                  {deviceType !== 'connector' && <option value="hub">Hub</option>}
                  <option value="spoke">Spoke</option>
                </select>
                {deviceType === 'connector' && (
                  <p className="text-xs text-gray-500 mt-1">Connectors can only be spoke members</p>
                )}
              </div>
            )}

            {topologyType === 'partial-mesh' && existingMembers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Link to Peers (optional)
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Select existing members to create links with. You can also manage links later.
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {existingMembers.map((m) => (
                    <label key={m.id} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                      <input
                        type="checkbox"
                        checked={selectedPeers.includes(m.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPeers([...selectedPeers, m.id]);
                          } else {
                            setSelectedPeers(selectedPeers.filter((p) => p !== m.id));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-900 dark:text-white">
                        {m.edgeName || m.edge_id}
                      </span>
                      <span className="text-xs text-gray-500">{m.tunnel_ip}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {redundancyMode !== 'single' && deviceType === 'edge' && selectedEdge && (
              <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
                  <Shield className="w-4 h-4" />
                  WAN Interface Binding
                </div>
                {loadingWan ? (
                  <div className="text-sm text-gray-500">Loading WAN interfaces...</div>
                ) : wanInterfaces.length === 0 ? (
                  <div className="text-sm text-yellow-600">No WAN interfaces found for this edge</div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Primary WAN (Tunnel 1)
                      </label>
                      <select
                        value={primaryWanPortId}
                        onChange={(e) => setPrimaryWanPortId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      >
                        {wanInterfaces.map((wan) => (
                          <option key={wan.id} value={wan.id}>
                            {wan.portName} - {wan.logicalName} ({wan.ipv4 || 'No IP'})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Secondary WAN (Tunnel 2)
                      </label>
                      <select
                        value={secondaryWanPortId}
                        onChange={(e) => setSecondaryWanPortId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      >
                        {wanInterfaces.filter((w) => w.id !== primaryWanPortId).map((wan) => (
                          <option key={wan.id} value={wan.id}>
                            {wan.portName} - {wan.logicalName} ({wan.ipv4 || 'No IP'})
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedDeviceId}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {deviceType === 'connector' ? 'Add Connector' : deviceType === 'mtge' ? 'Add Gateway' : 'Add Edge'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'active':
    case 'connected':
    case 'up':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'degraded':
    case 'error':
    case 'down':
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    case 'pending':
    case 'provisioning':
    case 'configured':
      return <Clock className="w-4 h-4 text-yellow-500" />;
    default:
      return <XCircle className="w-4 h-4 text-gray-400" />;
  }
};

// Add Link Modal (partial-mesh)
interface AddLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (memberAId: string, memberBId: string) => void;
  members: Array<{ id: string; edge_id: string; edgeName?: string; tunnel_ip: string }>;
  existingLinks: Array<{ member_a_id: string; member_b_id: string }>;
}

const AddLinkModal = ({ isOpen, onClose, onSubmit, members, existingLinks }: AddLinkModalProps) => {
  const [memberAId, setMemberAId] = useState('');
  const [memberBId, setMemberBId] = useState('');

  // Build set of existing linked pairs
  const linkedPairs = new Set(
    existingLinks.map((l) => [l.member_a_id, l.member_b_id].sort().join('-'))
  );

  const canLink = (aId: string, bId: string) => {
    if (!aId || !bId || aId === bId) return false;
    const key = [aId, bId].sort().join('-');
    return !linkedPairs.has(key);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Add Link
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Create a peering link between two members
          </p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(memberAId, memberBId); }}>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Member A
              </label>
              <select
                required
                value={memberAId}
                onChange={(e) => setMemberAId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a member...</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.edgeName || m.edge_id} ({m.tunnel_ip})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Member B
              </label>
              <select
                required
                value={memberBId}
                onChange={(e) => setMemberBId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a member...</option>
                {members
                  .filter((m) => m.id !== memberAId)
                  .map((m) => (
                    <option key={m.id} value={m.id} disabled={!canLink(memberAId, m.id)}>
                      {m.edgeName || m.edge_id} ({m.tunnel_ip}){!canLink(memberAId, m.id) ? ' (already linked)' : ''}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canLink(memberAId, memberBId)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Create Link
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

const EdgePeeringPage = () => {
  const toast = useToast();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [peerings, setPeerings] = useState<EdgePeering[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedPeering, setSelectedPeering] = useState<EdgePeering | null>(null);
  const [peeringStatus, setPeeringStatus] = useState<any>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; peering?: EdgePeering }>({ show: false });
  const [syncing, setSyncing] = useState(false);
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [memberRemoveConfirm, setMemberRemoveConfirm] = useState<{ show: boolean; edgeId?: string; edgeName?: string }>({ show: false });
  const [syncResults, setSyncResults] = useState<SyncResult | null>(null);

  // Fetch peerings and edges
  const fetchData = async () => {
    setLoading(true);
    try {
      const [peeringsData, edgesData] = await Promise.all([
        edgePeeringService.listPeerings(),
        edgeService.list({ pageSize: 100 }),
      ]);
      setPeerings(peeringsData.items || []);
      setEdges(edgesData.items);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load peering data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch status for selected peering
  const fetchPeeringStatus = async (peeringId: string) => {
    try {
      const status = await edgePeeringService.getPeeringStatus(peeringId);
      setPeeringStatus(status);
    } catch (error) {
      console.error('Failed to fetch peering status:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedTenantId]);

  useEffect(() => {
    if (selectedPeering) {
      fetchPeeringStatus(selectedPeering.id);
      const interval = setInterval(() => fetchPeeringStatus(selectedPeering.id), 30000);
      return () => clearInterval(interval);
    }
  }, [selectedPeering]);

  const handleCreatePeering = async (data: any) => {
    try {
      await edgePeeringService.createPeering({
        name: data.name,
        topologyType: data.topology_type,
        hubEdgeId: data.hub_edge_id || undefined,
        // VSNP-1019: omit when empty so the API allocates from the tenant's
        // peering subnet. Sending '' would fail CIDR validation, and sending a
        // made-up default is what put this tenant outside its own space.
        tunnelSubnet: data.tunnel_subnet || undefined,
        listenPort: data.listen_port,
        bgpEnabled: data.bgp_enabled,
        bgpMode: data.bgp_mode,
        baseAsn: data.base_asn,
        bfdEnabled: data.bfd_enabled,
        dpsEnabled: data.dps_enabled,
        dpsProbeIntervalMs: data.dps_probe_interval_ms,
        dpsMaxLatencyMs: data.dps_max_latency_ms,
        dpsMaxJitterMs: data.dps_max_jitter_ms,
        dpsMaxLossPct: data.dps_max_loss_pct,
        dpsSwitchThreshold: data.dps_switch_threshold,
        dpsRecoveryThreshold: data.dps_recovery_threshold,
        redundancyMode: data.redundancy_mode,
        secondaryTunnelSubnet: data.secondary_tunnel_subnet || undefined,
        secondaryListenPort: data.secondary_listen_port,
        tunnelProtocol: data.tunnel_protocol,
        ipsecRekeyIntervalHours: data.tunnel_protocol === 'ipsec' ? data.ipsec_rekey_interval_hours : undefined,
      } as any);
      toast.success('Peering topology created successfully');
      setIsCreateModalOpen(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create peering');
    }
  };

  const handleEditPeering = async (data: any) => {
    if (!selectedPeering) return;
    try {
      await edgePeeringService.updatePeering(selectedPeering.id, {
        name: data.name,
        bgpEnabled: data.bgp_enabled,
        bgpMode: data.bgp_mode,
        bfdEnabled: data.bfd_enabled,
        dpsEnabled: data.dps_enabled,
        dpsProbeIntervalMs: data.dps_probe_interval_ms,
        dpsMaxLatencyMs: data.dps_max_latency_ms,
        dpsMaxJitterMs: data.dps_max_jitter_ms,
        dpsMaxLossPct: data.dps_max_loss_pct,
        dpsSwitchThreshold: data.dps_switch_threshold,
        dpsRecoveryThreshold: data.dps_recovery_threshold,
        redundancyMode: data.redundancy_mode,
        secondaryTunnelSubnet: data.secondary_tunnel_subnet || undefined,
        secondaryListenPort: data.secondary_listen_port,
        tunnelProtocol: data.tunnel_protocol,
        ipsecRekeyIntervalHours: data.tunnel_protocol === 'ipsec' ? data.ipsec_rekey_interval_hours : undefined,
      } as any);
      toast.success('Peering topology updated successfully');
      setIsEditModalOpen(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update peering');
    }
  };

  const handleDeletePeering = async () => {
    if (!deleteConfirm.peering) return;
    try {
      await edgePeeringService.deletePeering(deleteConfirm.peering.id);
      toast.success('Peering topology deleted');
      setDeleteConfirm({ show: false });
      if (selectedPeering?.id === deleteConfirm.peering.id) {
        setSelectedPeering(null);
        setPeeringStatus(null);
      }
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete peering');
    }
  };

  const handleAddMember = async (deviceId: string, role: string, deviceType: 'edge' | 'mtge' | 'connector', primaryWanPortId?: string, secondaryWanPortId?: string, peerWith?: string[]) => {
    if (!selectedPeering) return;
    try {
      const payload: any = {
        role: role as 'hub' | 'spoke' | 'mesh-peer',
        primaryWanPortId,
        secondaryWanPortId,
        peerWith,
      };
      if (deviceType === 'connector') {
        payload.connectorId = deviceId;
      } else if (deviceType === 'mtge') {
        payload.mtgeId = deviceId;
      } else {
        payload.edgeId = deviceId;
      }
      await edgePeeringService.addMember(selectedPeering.id, payload);
      toast.success(
        deviceType === 'connector' ? 'Connector added to peering' :
        deviceType === 'mtge' ? 'MTGE added to peering' : 'Edge added to peering'
      );
      setIsAddMemberModalOpen(false);
      fetchPeeringStatus(selectedPeering.id);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add member');
    }
  };

  const handleRemoveMember = async () => {
    if (!selectedPeering || !memberRemoveConfirm.edgeId) return;
    try {
      await edgePeeringService.removeMember(selectedPeering.id, memberRemoveConfirm.edgeId);
      toast.success('Edge removed from peering');
      setMemberRemoveConfirm({ show: false });
      fetchPeeringStatus(selectedPeering.id);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to remove edge');
    }
  };

  const handleSyncPeering = async () => {
    if (!selectedPeering) return;
    setSyncing(true);
    try {
      const result = await edgePeeringService.syncPeering(selectedPeering.id);
      setSyncResults(result);
      if (result.sync?.attempted) {
        fetchPeeringStatus(selectedPeering.id);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to sync peering');
    } finally {
      setSyncing(false);
    }
  };

  const handleAddLink = async (memberAId: string, memberBId: string) => {
    if (!selectedPeering) return;
    try {
      await edgePeeringService.addLink(selectedPeering.id, memberAId, memberBId);
      toast.success('Link created successfully');
      setIsAddLinkModalOpen(false);
      fetchPeeringStatus(selectedPeering.id);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create link');
    }
  };

  const handleRemoveLink = async (linkId: string) => {
    if (!selectedPeering) return;
    try {
      await edgePeeringService.removeLink(selectedPeering.id, linkId);
      toast.success('Link removed successfully');
      fetchPeeringStatus(selectedPeering.id);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to remove link');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Edge Peering</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage edge-to-edge tunnels and BGP route propagation
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {canCreate && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Create Peering
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Peerings"
          value={peerings.length}
          icon={Network}
          color="primary"
        />
        <StatCard
          title="Active"
          value={peerings.filter(p => p.status === 'active').length}
          icon={CheckCircle}
          color="success"
        />
        <StatCard
          title="Degraded"
          value={peerings.filter(p => p.status === 'degraded').length}
          icon={AlertCircle}
          color="warning"
        />
        <StatCard
          title="Pending"
          value={peerings.filter(p => p.status === 'pending' || p.status === 'provisioning').length}
          icon={Clock}
          color="secondary"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Peerings List */}
        <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Peering Topologies</h2>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
            {peerings.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No peering topologies configured
              </div>
            ) : (
              peerings.map((peering) => (
                <div
                  key={peering.id}
                  onClick={() => setSelectedPeering(peering)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedPeering?.id === peering.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Network className="w-5 h-5 text-blue-500" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{peering.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {peering.topology_type} - {peering.tunnel_subnet}
                          {peering.tunnel_protocol === 'ipsec' && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">IPSec</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(peering.status)}
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Peering Details */}
        <div className="lg:col-span-2">
          {selectedPeering ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedPeering.name}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Edge-to-Edge Peering Topology
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {canUpdate && (
                    <button
                      onClick={() => setIsEditModalOpen(true)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                      title="Edit peering settings"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {canUpdate && (
                    <button
                      onClick={handleSyncPeering}
                      disabled={syncing}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      <Play className={`w-4 h-4 ${syncing ? 'animate-pulse' : ''}`} />
                      {syncing ? 'Syncing...' : 'Sync'}
                    </button>
                  )}
                  {canUpdate && selectedPeering?.tunnel_protocol === 'ipsec' && (
                    <button
                      onClick={async () => {
                        try {
                          const result = await edgePeeringService.rekeyIPSec(selectedPeering.id);
                          toast.success(`Rekeyed ${result.linksRekeyed} links`);
                        } catch (err: any) {
                          toast.error(err?.message || 'Failed to rekey');
                        }
                      }}
                      className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                    >
                      Rekey IPSec
                    </button>
                  )}
                  {canCreate && (
                    <button
                      onClick={() => setIsAddMemberModalOpen(true)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      Add Edge
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => setDeleteConfirm({ show: true, peering: selectedPeering })}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Configuration Summary */}
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500 dark:text-gray-400">Topology:</span>
                  <span className="font-medium text-gray-900 dark:text-white capitalize">{selectedPeering.topology_type}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500 dark:text-gray-400">Tunnel Protocol:</span>
                  <span className={`font-medium ${selectedPeering.tunnel_protocol === 'ipsec' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>
                    {selectedPeering.tunnel_protocol === 'ipsec' ? 'IPSec (AES-GCM-256)' : 'WireGuard'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500 dark:text-gray-400">Links:</span>
                  <span className="font-medium text-gray-900 dark:text-white capitalize">
                    {selectedPeering.redundancy_mode === 'active-standby' ? 'Dual (Active/Standby)' :
                      selectedPeering.redundancy_mode === 'active-active' ? 'Dual (Active/Active)' : 'Single'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500 dark:text-gray-400">Subnet:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{selectedPeering.tunnel_subnet}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500 dark:text-gray-400">Port:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{selectedPeering.listen_port}</span>
                </div>
                {selectedPeering.bgp_enabled ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-500 dark:text-gray-400">BGP:</span>
                    <span className="font-medium text-gray-900 dark:text-white uppercase">{selectedPeering.bgp_mode || 'ibgp'}</span>
                    {selectedPeering.bfd_enabled ? <span className="text-gray-500 dark:text-gray-400">+ BFD</span> : null}
                    {selectedPeering.dps_enabled ? <span className="text-green-600 dark:text-green-400">+ DPS</span> : null}
                  </div>
                ) : null}
              </div>

              {/* DPS Path Quality Card */}
              {/* Coerce dps_enabled to boolean — it's a tinyint (0|1) so the
                  bare `&&` chain renders the literal number 0 when DPS is
                  off, leaking a stray "0" between Config Summary and Health
                  Summary. */}
              {!!selectedPeering.dps_enabled && peeringStatus?.dpsStats && (
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <Activity className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">Path Quality (DPS)</span>
                    {peeringStatus.dpsStats.switched > 0 && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 rounded-full">
                        {peeringStatus.dpsStats.switched} switched
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                      <div className="text-lg font-bold text-green-700 dark:text-green-400">{peeringStatus.dpsStats.healthy}</div>
                      <div className="text-xs text-gray-500">Healthy</div>
                    </div>
                    <div className="text-center p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                      <div className="text-lg font-bold text-yellow-700 dark:text-yellow-400">{peeringStatus.dpsStats.degraded}</div>
                      <div className="text-xs text-gray-500">Degraded</div>
                    </div>
                    <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                      <div className="text-lg font-bold text-blue-700 dark:text-blue-400">{peeringStatus.dpsStats.switched}</div>
                      <div className="text-xs text-gray-500">Switched</div>
                    </div>
                  </div>
                  {/* Per-link path quality details */}
                  {peeringStatus.links && peeringStatus.links.some((l: any) => l.dps_state) && (
                    <div className="mt-3 space-y-2">
                      {peeringStatus.links.filter((l: any) => l.dps_state).map((link: any) => (
                        <div key={link.id} className="flex items-center justify-between text-xs p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600 dark:text-gray-400">
                              {link.member_a_name} &harr; {link.member_b_name}
                            </span>
                            <span className="text-gray-400">T{link.tunnel_index}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            {link.latency_ms != null && (
                              <span className={`${parseFloat(link.latency_ms) > (selectedPeering.dps_max_latency_ms || 150) ? 'text-red-600' : 'text-gray-600 dark:text-gray-400'}`}>
                                {parseFloat(link.latency_ms).toFixed(1)}ms
                              </span>
                            )}
                            {link.jitter_ms != null && (
                              <span className={`${parseFloat(link.jitter_ms) > (selectedPeering.dps_max_jitter_ms || 30) ? 'text-red-600' : 'text-gray-600 dark:text-gray-400'}`}>
                                J:{parseFloat(link.jitter_ms).toFixed(1)}ms
                              </span>
                            )}
                            {link.packet_loss_pct != null && (
                              <span className={`${parseFloat(link.packet_loss_pct) > (selectedPeering.dps_max_loss_pct || 1) ? 'text-red-600' : 'text-gray-600 dark:text-gray-400'}`}>
                                {parseFloat(link.packet_loss_pct).toFixed(1)}%
                              </span>
                            )}
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              link.dps_state === 'healthy' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                              'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                            }`}>
                              {link.dps_state}
                            </span>
                            {link.dps_switched && (
                              <span className="text-yellow-600 dark:text-yellow-400" title={link.dps_switch_reason}>
                                ABF
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Health Summary */}
              {peeringStatus && (
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {peeringStatus.health?.connected_members || 0}/{peeringStatus.health?.total_members || 0}
                    </div>
                    <div className="text-sm text-gray-500">Members Connected</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {peeringStatus.health?.links_up || 0}/{peeringStatus.health?.total_links || 0}
                    </div>
                    <div className="text-sm text-gray-500">Links Up</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {peeringStatus.health?.bgp_sessions_established || 0}
                    </div>
                    <div className="text-sm text-gray-500">BGP Sessions</div>
                  </div>
                  <div className="text-center">
                    {(() => {
                      // Compute actual status from health data
                      const health = peeringStatus.health;
                      const connectedMembers = health?.connected_members || 0;
                      const totalMembers = health?.total_members || 0;
                      const linksUp = health?.links_up || 0;
                      const totalLinks = health?.total_links || 0;

                      let computedStatus = 'unknown';
                      if (totalMembers === 0) {
                        computedStatus = 'pending';
                      } else if (connectedMembers === 0 || (totalLinks > 0 && linksUp === 0)) {
                        computedStatus = 'down';
                      } else if (connectedMembers < totalMembers || linksUp < totalLinks) {
                        computedStatus = 'degraded';
                      } else {
                        computedStatus = 'active';
                      }

                      const statusColor = computedStatus === 'active' ? 'text-green-600' :
                        computedStatus === 'degraded' ? 'text-yellow-600' :
                          computedStatus === 'down' ? 'text-red-600' : 'text-gray-600';

                      return (
                        <>
                          <div className={`text-2xl font-bold ${statusColor}`}>
                            {computedStatus.toUpperCase()}
                          </div>
                          <div className="text-sm text-gray-500">Status</div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Members */}
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Members</h3>
                <div className="space-y-2">
                  {peeringStatus?.members?.length > 0 ? (
                    peeringStatus.members.map((member: any) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {getStatusIcon(member.status)}
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                              {member.edge_name || member.edge_id}
                              {member.member_type === 'connector' && (
                                <span className="px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                                  Connector
                                </span>
                              )}
                              {member.member_type === 'mtge' && (
                                <span className="px-1.5 py-0.5 text-xs rounded bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                                  MTGE
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {member.role} - {member.tunnel_ip}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 font-mono">
                            {member.learned_endpoint || 'No endpoint'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {selectedPeering?.tunnel_protocol === 'ipsec'
                              ? (member.status === 'connected' || member.status === 'active' ? 'SA Active' : 'SA Pending')
                              : (member.last_handshake ? formatDate(member.last_handshake) : '')}
                          </span>
                          {canDelete && (
                            <button
                              onClick={() => setMemberRemoveConfirm({ show: true, edgeId: member.edge_id, edgeName: member.edge_name || member.edge_id })}
                              className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-4">
                      No members added yet
                    </div>
                  )}
                </div>
              </div>

              {/* Links */}
              {(peeringStatus?.links?.length > 0 || selectedPeering?.topology_type === 'partial-mesh') && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Links</h3>
                    {canCreate && selectedPeering?.topology_type === 'partial-mesh' && peeringStatus?.members?.length >= 2 && (
                      <button
                        onClick={() => setIsAddLinkModalOpen(true)}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        <Plus className="w-3 h-3" />
                        Add Link
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {peeringStatus?.links?.length > 0 ? (
                      peeringStatus.links.map((link: any) => (
                        <div
                          key={link.id}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <Link2 className={`w-4 h-4 ${link.tunnel_status === 'up' ? 'text-green-500' : 'text-red-500'}`} />
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {link.member_a_name || link.member_a_id} ↔ {link.member_b_name || link.member_b_id}
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <span>BGP: {link.bgp_session_state || 'N/A'}</span>
                                {selectedPeering?.tunnel_protocol === 'ipsec' && (
                                  <span>SA: {link.tunnel_status === 'up' ? 'Established' : 'Down'}</span>
                                )}
                                {link.tunnel_index !== undefined && link.tunnel_index !== null && link.tunnel_index > 0 && (
                                  <span className="px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                    T{link.tunnel_index + 1}
                                  </span>
                                )}
                                {link.tunnel_index === 0 && selectedPeering?.redundancy_mode !== 'single' && (
                                  <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                    T1
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusBadge status={link.tunnel_status === 'up' ? 'Online' : 'Offline'} />
                            {canDelete && selectedPeering?.topology_type === 'partial-mesh' && (
                              <button
                                onClick={() => handleRemoveLink(link.id)}
                                className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                title="Remove link"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500 py-4">
                        No links yet. Add links to connect members.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500">
              <Network className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>Select a peering topology to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <PeeringModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreatePeering}
        edges={edges}
      />

      {selectedPeering && (
        <AddMemberModal
          isOpen={isAddMemberModalOpen}
          onClose={() => setIsAddMemberModalOpen(false)}
          onSubmit={handleAddMember}
          edges={edges}
          existingMemberIds={peeringStatus?.members?.map((m: any) => m.edge_id) || []}
          existingMembers={peeringStatus?.members?.map((m: any) => ({ id: m.id, edge_id: m.edge_id, edgeName: m.edgeName || m.edge_name, tunnel_ip: m.tunnel_ip })) || []}
          topologyType={selectedPeering.topology_type}
          redundancyMode={selectedPeering.redundancy_mode || 'single'}
        />
      )}

      {selectedPeering?.topology_type === 'partial-mesh' && (
        <AddLinkModal
          isOpen={isAddLinkModalOpen}
          onClose={() => setIsAddLinkModalOpen(false)}
          onSubmit={handleAddLink}
          members={peeringStatus?.members?.map((m: any) => ({ id: m.id, edge_id: m.edge_id, edgeName: m.edgeName || m.edge_name, tunnel_ip: m.tunnel_ip })) || []}
          existingLinks={peeringStatus?.links?.filter((l: any) => l.tunnel_index === 0 || l.tunnel_index === undefined) || []}
        />
      )}

      {selectedPeering && (
        <PeeringModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditPeering}
          edges={edges}
          initialData={selectedPeering}
        />
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.show}
        title="Delete Peering Topology"
        message={`Are you sure you want to delete "${deleteConfirm.peering?.name}"? This will remove all tunnels and BGP sessions.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeletePeering}
        onClose={() => setDeleteConfirm({ show: false })}
      />

      <ConfirmDialog
        isOpen={memberRemoveConfirm.show}
        title="Remove Member"
        message={`Are you sure you want to remove "${memberRemoveConfirm.edgeName}" from this peering? Its tunnels and BGP sessions will be torn down.`}
        confirmText="Remove"
        variant="danger"
        onConfirm={handleRemoveMember}
        onClose={() => setMemberRemoveConfirm({ show: false })}
      />

      {/* Sync Results Dialog */}
      {syncResults && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSyncResults(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Sync Config Results
              </h2>
              <button
                onClick={() => setSyncResults(null)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-6">
              {/* Pre-flight Checks */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                  Pre-flight Checks
                </h3>
                <div className="space-y-2">
                  {syncResults.preflight.checks.map((check, i) => (
                    <div
                      key={i}
                      className={`flex items-start gap-3 p-3 rounded-lg text-sm ${check.level === 'error'
                          ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                          : check.level === 'warning'
                            ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                            : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                        }`}
                    >
                      {check.level === 'error' ? (
                        <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      ) : check.level === 'warning' ? (
                        <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <span className="font-medium text-gray-900 dark:text-white">{check.member}</span>
                        <span className="mx-2 text-gray-400">—</span>
                        <span className={
                          check.level === 'error'
                            ? 'text-red-700 dark:text-red-300'
                            : check.level === 'warning'
                              ? 'text-yellow-700 dark:text-yellow-300'
                              : 'text-green-700 dark:text-green-300'
                        }>
                          {check.message}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {!syncResults.preflight.canSync && (
                  <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg text-sm text-red-800 dark:text-red-200 flex items-center gap-2">
                    <XCircle className="w-4 h-4 flex-shrink-0" />
                    Sync blocked — resolve the errors above before syncing.
                  </div>
                )}
              </div>

              {/* Sync Results */}
              {syncResults.sync?.attempted && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                    Sync Results
                  </h3>
                  <div className="space-y-2">
                    {syncResults.sync.results.map((r, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-3 p-3 rounded-lg text-sm ${r.success
                            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                          }`}
                      >
                        {r.success ? (
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                        <span className="font-medium text-gray-900 dark:text-white">{r.member}</span>
                        <span className={r.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                          {r.success ? 'Config published' : r.error || 'Failed'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    Config published via MQTT. Edges will apply and confirm within ~30 seconds.
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setSyncResults(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default EdgePeeringPage;
